/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Builds question preview view data from the eText TOC and the chapter MCQ mapping,
 * using the same `tocData` / `chapterMcqMapping` contract as the add-manually and
 * assisted scheduling content trees.
 *
 * @file questionPreviewUtils.js
 * @author Anuja Gaikwad
 * @since 3.0.0
 */

import { calculateQuestionsForNode } from '../chapter-content/chapterContentTreeUtils';

const CHAPTER_NODE_TYPE = 'chapter';

/**
 * Top-level chapters of the TOC; these populate the chapter dropdown.
 * @param {Array} tocData TOC tree (`[{ id, title, type, children }]`)
 * @returns {Array} chapter nodes
 */
export const getTocChapters = (tocData = []) => (
  (tocData || []).filter((node) => node?.type === CHAPTER_NODE_TYPE)
);

/** Ids of a node and all its descendants, used to match questions (by `pageId`) to a section. */
const collectNodeIds = (node, ids = new Set()) => {
  if (!node?.id) {
    return ids;
  }

  ids.add(node.id);
  (node.children || []).forEach((child) => collectNodeIds(child, ids));

  return ids;
};

/** Accepts both raw MCQ options (`distractor` / `correctChoice`) and mapped options (`label` / `correct`). */
export const mapPreviewOptions = (options = []) => (options || []).map((option, index) => ({
  id: option.id ?? index,
  label: option.label ?? option.distractor ?? '',
  correct: typeof option.correct === 'boolean' ? option.correct : option.correctChoice === 'Yes'
}));

/**
 * Normalizes a question to `{ questionId, stem, options }` for QuestionPreviewCard.
 * Accepts the raw MCQ shape (`questionStem`) and the Questions & Topics modal shape (`stem`).
 */
export const mapPreviewQuestion = (question) => ({
  questionId: question.questionId,
  stem: question.stem ?? question.questionStem ?? '',
  options: mapPreviewOptions(question.options)
});

/**
 * Dropdown options for every TOC chapter. Counts use the same helper as the add-manually
 * content tree, so both screens show identical numbers.
 * @returns {Array<{ id: string, title: string, questionCount: number }>}
 */
export const buildChapterOptions = (tocData = [], chapterMcqMapping = []) => (
  getTocChapters(tocData).map((chapter) => ({
    id: chapter.id,
    title: chapter.title,
    questionCount: calculateQuestionsForNode(chapter, chapterMcqMapping, tocData)
  }))
);

/** Initial selection: the first chapter with questions (front matter like Preface has none), else the first chapter. */
export const getDefaultChapterId = (chapterOptions = []) => (
  (chapterOptions.find((chapter) => chapter.questionCount > 0) || chapterOptions[0])?.id || ''
);

/**
 * Groups a chapter's questions under its direct TOC children (sections). Questions on
 * nested pages roll up into their top-level section. All direct child sections are included
 * (including zero-question rows), matching the add/edit content tree.
 */
export const buildChapterSections = (chapterNode, chapterMcqMapping = []) => {
  if (!chapterNode) {
    return [];
  }

  const chapterQuestions = (chapterMcqMapping || [])
    .find((entry) => entry.chapterId === chapterNode.id)?.questions || [];
  const sectionNodes = chapterNode.children?.length ? chapterNode.children : [chapterNode];

  return sectionNodes
    .map((sectionNode) => {
      const pageIds = collectNodeIds(sectionNode);
      const questions = chapterQuestions
        .filter((question) => pageIds.has(question.pageId))
        .map(mapPreviewQuestion);

      return {
        id: sectionNode.id,
        title: sectionNode.title,
        questionCount: questions.length,
        questions
      };
    });
};

/** "1 question" / "3 questions" using the host's localized singular and plural labels. */
export const formatQuestionCount = (count, textInfo = {}) => {
  const label = count === 1 ? textInfo.questionLabel : textInfo.questionsLabel;

  return `${count} ${label}`;
};

/** Option badge letter: 0 -> "A", 1 -> "B", ... */
export const getOptionLetter = (index) => String.fromCharCode(65 + index);
